import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { GET_PREVIEW_MEETINGS_OPTIMIZED } from '../../graphql/queries/previewMeetingsOptimized';
import { useTrack } from '../track';
import { useTourSteps } from '../tour';
import { useBrand } from '../brand';
import { addDays, datePath, isoDate, parseDatePath } from '../lib/dates';
import { longDay } from '../lib/format';
import { PREF, useStickyPref } from '../lib/prefs';
import { Legend, MeetingGroups, TodaySoFar } from './components';
import { LocksBanner } from './LocksBanner';
import { useDayAgreement } from './useDayAgreement';
import { categoryRoi, categoryValueRoi, dayTotals, racesRun, type HomeMeeting } from './lib';
import './home.css';

/** How far back signed-out visitors can look: yesterday to 14 days ago, as the classic preview allowed. */
export const PREVIEW_DAYS = 14;

/**
 * The preview window around `now`: the day to show for a /preview/YYYYMMDD
 * path (yesterday if missing or out of range) and whether it is at either end.
 */
export function previewDay(dateString: string | undefined, now: Date = new Date()) {
  const noon = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  const latest = addDays(noon, -1);
  const earliest = addDays(noon, -PREVIEW_DAYS);
  const asked = parseDatePath(dateString);
  const inRange = !!asked && isoDate(asked) >= isoDate(earliest) && isoDate(asked) <= isoDate(latest);
  const day = inRange ? asked! : latest;
  return { day, valid: inRange, atEarliest: isoDate(day) === isoDate(earliest), atLatest: isoDate(day) === isoDate(latest) };
}

/**
 * The top of a signed-out preview page: title, the day, previous/next day
 * within the preview window (under `base`, e.g. /preview), and the sign-up prompt.
 */
export function PreviewBar({ title, base, day, atEarliest, atLatest }: { title: string; base: string; day: Date; atEarliest: boolean; atLatest: boolean }) {
  const navigate = useNavigate();
  const track = useTrack();
  const go = (n: number) => {
    track('ui_click', { target: 'date_nav', direction: n < 0 ? 'previous' : 'next' });
    navigate(`${base}/${datePath(addDays(day, n))}`);
  };
  return (
    <>
      <div className="ui-pvh" data-tour="preview">
        <div>
          <h1>{title}</h1>
          <p>{longDay(isoDate(day))}</p>
        </div>
        <div className="nav">
          <button type="button" className="ui-home-btn quiet" disabled={atEarliest} onClick={() => go(-1)}>
            ‹ Previous day
          </button>
          <button type="button" className="ui-home-btn quiet" disabled={atLatest} onClick={() => go(1)}>
            Next day ›
          </button>
        </div>
      </div>

      <div className="ui-pvcta">
        <span>
          <b>You&apos;re viewing past tips.</b> Sign up to see today&apos;s live tips and upcoming races.
        </span>
        <Link to="/register" className="ui-home-btn primary" onClick={() => track('ui_click', { target: 'preview_signup' })}>
          Get today&apos;s tips
        </Link>
      </div>
    </>
  );
}

/** The tour's opening step on a preview page: what a visitor can see and how to move between days. */
export const PREVIEW_TOUR_STEP = {
  target: 'preview',
  title: 'Past days',
  body: `Look back at how our tips went on any of the last ${PREVIEW_DAYS} days, with Previous day and Next day. Sign up for today's tips.`,
};

/** The signed-out preview (/preview/:dateString): a past day's meetings, results and a way to sign up. */
export function PreviewView() {
  const { dateString } = useParams<{ dateString?: string }>();
  const navigate = useNavigate();
  const track = useTrack();
  const { day, valid, atEarliest, atLatest } = previewDay(dateString);
  const iso = isoDate(day);
  const [tw, setTw] = useStickyPref(PREF.tipsWonOnCards, 'on', ['on', 'off'] as const);

  // an out-of-range or missing date goes to yesterday, as before
  useEffect(() => {
    if (!valid) navigate(`/preview/${datePath(day)}`, { replace: true });
  }, [valid, day, navigate]);

  const q = useQuery<{ previewMeetingsOptimized: HomeMeeting[] }>(GET_PREVIEW_MEETINGS_OPTIMIZED, {
    variables: { date: iso, analysisTypeId: 1 },
    skip: !valid,
    fetchPolicy: 'cache-first',
  });
  const meetings = q.data?.previewMeetingsOptimized ?? [];
  const { agree } = useDayAgreement(iso, !valid);
  const brand = useBrand();

  useTourSteps([
    PREVIEW_TOUR_STEP,
    { target: 'locks', title: brand.locksName, body: "Our strongest runners of the day, each rated a 55% chance or better. Tap it to see how this day's picks went." },
    { target: 'sofar', title: 'On the day', body: 'How the day went: top pick strike rate and returns. Tap it for quinellas, trifectas, quaddies and more.' },
    { target: 'legend', title: 'The key', body: "What the bar colours and the markers mean. The Show on cards switch puts which tips won above every meeting's bars." },
    { target: 'groups', title: 'Track groups', body: "Meetings are grouped Metropolitan, Provincial and Country, with each group's return on the day." },
    { target: 'meeting', title: 'A meeting', body: "Top pick and any-tip winners, one bar per race (green: a tip won, red: none), and the meeting's returns. Tap Tips & results to open it." },
  ]);

  const tracked = useRef<string | null>(null);
  useEffect(() => {
    if (!valid || q.loading || tracked.current === iso) return;
    tracked.current = iso;
    track('page_view', { pageType: 'preview', selectedDate: iso, metadata: { meetingsCount: meetings.length, isPreview: true } });
  }, [valid, q.loading, iso, meetings.length, track]);

  return (
    <div className="ui-home ui-preview" data-twall={tw === 'on'}>
      <PreviewBar title="Past tips" base="/preview" day={day} atEarliest={atEarliest} atLatest={atLatest} />
      <LocksBanner to={`/preview/picks/${datePath(day)}`} cta="See this day's picks" />

      {q.loading && <p className="ui-home-note">Loading meetings…</p>}
      {q.error && <p className="ui-home-note bad">Error loading meetings: {q.error.message}</p>}

      {meetings.length > 0 && (
        <>
          <TodaySoFar totals={dayTotals(meetings)} roi={categoryRoi(meetings)} valueRoi={categoryValueRoi(meetings)} label="On the day" agree={agree} />
          <section className="ui-home-sec">
            <div className="ui-home-sh">
              <h2>Meetings</h2>
            </div>
            <Legend
              showSwitch={meetings.some((m) => racesRun(m) > 0)}
              on={tw === 'on'}
              onToggle={() => {
                const next = tw === 'on' ? 'off' : 'on';
                setTw(next);
                track('ui_toggle', { control: 'show_won_on_cards', value: next });
              }}
            />
            <MeetingGroups meetings={meetings} tour />
          </section>
          {brand.features.publicRacingPages && (
            <p className="ui-home-note">
              <a href={`/horse-racing-tips/${iso}`}>Every meeting on {longDay(iso)}, with our tips and the results</a>
            </p>
          )}
        </>
      )}
      {!q.loading && !q.error && q.data && meetings.length === 0 && <p className="ui-home-note">No meetings with tips on this day.</p>}
    </div>
  );
}
